interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  g: number;
  size: number;
  rot: number;
  vr: number;
  color: string;
  life: number;
  ttl: number;
  shape: 'rect' | 'circ';
}

// Brand-aligned palette (ATS amber + greens + teal accent). Drops the off-brand
// bright blue/orange so the burst reads as part of the RealREPP theme.
const CONFETTI_COLORS = [
  '#eaa827', // amber (brand / Clear button)
  '#28a745', // green (Paid)
  '#CC8826', // amber-dark
  '#308d3f', // green-dark
  '#2e95b4', // teal accent
  '#FBF1DE', // soft cream
];

let canvas: HTMLCanvasElement | null = null;
let ctx: CanvasRenderingContext2D | null = null;
let particles: Particle[] = [];
let rafId: number | null = null;

function getOrCreateCanvas(): HTMLCanvasElement | null {
  if (canvas && document.body.contains(canvas)) {
    return canvas;
  }
  // Remove any stray/orphaned confetti canvas (e.g. left over from a dev
  // hot-reload, which resets module state but leaves the old DOM element) so we
  // never stack multiple confetti layers on document.body.
  document
    .querySelectorAll('#placement-celebration-confetti')
    .forEach((el) => el.remove());
  canvas = document.createElement('canvas');
  canvas.id = 'placement-celebration-confetti';
  // z-index sits just BELOW the toast container (.container is 9999) so confetti
  // bursts from behind/around the toast without covering its lower half.
  canvas.style.cssText =
    'position:fixed;inset:0;width:100%;height:100%;pointer-events:none;z-index:9998;';
  document.body.appendChild(canvas);
  ctx = canvas.getContext('2d');
  sizeCanvas();
  window.addEventListener('resize', sizeCanvas);
  return canvas;
}

function sizeCanvas(): void {
  if (!canvas || !ctx) return;
  canvas.width = window.innerWidth * window.devicePixelRatio;
  canvas.height = window.innerHeight * window.devicePixelRatio;
  ctx.setTransform(
    window.devicePixelRatio,
    0,
    0,
    window.devicePixelRatio,
    0,
    0,
  );
}

function tick(): void {
  if (!ctx || !canvas) {
    rafId = null;
    return;
  }
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  particles = particles.filter((p) => p.life < p.ttl);

  for (const p of particles) {
    p.life++;
    p.vy += p.g;
    p.x += p.vx;
    p.y += p.vy;
    p.vx *= 0.99;
    p.rot += p.vr;

    const alpha = Math.max(0, 1 - p.life / p.ttl);
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.translate(p.x, p.y);
    ctx.rotate(p.rot);
    ctx.fillStyle = p.color;

    if (p.shape === 'rect') {
      ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
    } else {
      ctx.beginPath();
      ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }

  if (particles.length > 0) {
    rafId = requestAnimationFrame(tick);
  } else {
    rafId = null;
    if (ctx && canvas) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  }
}

/**
 * Hard-stop + clear the confetti canvas. Used as a safety net when all toasts
 * are gone: the self-terminating rAF loop normally clears itself, but if a frame
 * is dropped (e.g. during a heavy page navigation) the loop can halt with a
 * painted frame still on the canvas. Calling this guarantees no lingering streak.
 */
export function clearConfetti(): void {
  if (rafId !== null) {
    cancelAnimationFrame(rafId);
    rafId = null;
  }
  particles = [];
  window.removeEventListener('resize', sizeCanvas);
  // Physically remove the canvas element(s) — more robust than clearing pixels,
  // which can miss an orphaned canvas from a hot-reload whose context our module
  // no longer references. Sweeps by id so any stray layer is removed too.
  document
    .querySelectorAll('#placement-celebration-confetti')
    .forEach((el) => el.remove());
  canvas = null;
  ctx = null;
}

export function burstConfetti(x: number, y: number): void {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return;
  }

  getOrCreateCanvas();
  if (!ctx) return;

  const n = 90;
  for (let k = 0; k < n; k++) {
    const ang = Math.random() * Math.PI * 2;
    const spd = 2.4 + Math.random() * 4.5;
    particles.push({
      x,
      y,
      vx: Math.cos(ang) * spd,
      vy: Math.sin(ang) * spd - (2.4 + Math.random() * 2.4),
      g: 0.07 + Math.random() * 0.05,
      size: 5 + Math.random() * 5,
      rot: Math.random() * Math.PI,
      vr: (Math.random() - 0.5) * 0.25,
      color: CONFETTI_COLORS[k % CONFETTI_COLORS.length],
      life: 0,
      ttl: 130 + Math.random() * 70,
      shape: Math.random() < 0.5 ? 'rect' : 'circ',
    });
  }

  if (!rafId) {
    tick();
  }
}
